import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useSortedPaginated, SortableTh, Pagination, useFilters, FilterRow } from '../components/TableControls';
import { formatDate } from '../utils/dateUtils';
import useAutoSelectSingle from '../hooks/useAutoSelectSingle';
import InvalidateWarningModal from '../components/InvalidateWarningModal';

const CATEGORY_LABELS = {
  marketing: 'Marketing (eventos)',
  factory: 'Fábrica (defectuoso)',
  gifts: 'Regalos (a clientes)',
  other: 'Otro',
};

export default function Waste() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState({ store_id: '', item_id: '', quantity: '', reason: '', waste_category: '', date: new Date().toISOString().slice(0, 10) });
  const [msg, setMsg] = useState('');
  const [msgErr, setMsgErr] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [invalidate, setInvalidate] = useState(null);
  const { filters, setFilter, filtered } = useFilters(transactions, ['store_name', 'item_name', 'waste_category', 'user_name']);
  const { paginated, sortKey, sortDir, setSort, page, setPage, perPage, setPerPage, total } = useSortedPaginated(filtered, 'date', 'desc');

  const loadTxns = () => api.get('/transactions', { params: { transaction_type: 'waste' } }).then(r => setTransactions(r.data));

  useEffect(() => {
    api.get('/stores').then(r => setStores(r.data));
    api.get('/items').then(r => setItems(r.data));
    loadTxns();
  }, []);

  useAutoSelectSingle(stores, form.store_id, (id) => setForm(f => ({ ...f, store_id: id })));
  useAutoSelectSingle(items, form.item_id, (id) => setForm(f => ({ ...f, item_id: id })));

  const submit = (e) => { e.preventDefault(); doSubmit(false); };

  const doSubmit = async (force = false) => {
    setMsg(''); setMsgErr(false);
    try {
      await api.post(`/transactions/waste${force ? '?force=true' : ''}`, {
        store_id: parseInt(form.store_id),
        item_id: parseInt(form.item_id),
        quantity: parseInt(form.quantity, 10),
        reason: form.reason,
        waste_category: form.waste_category,
        date: form.date,
      });
      setMsg('¡Merma/desperdicio registrado!');
      setForm({ ...form, item_id: '', quantity: '', reason: '', waste_category: '' });
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doSubmit(true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`¿Eliminar esta entrada de merma de ${t.quantity} x ${t.item_name}?`)) return;
    doDelete(t, false);
  };

  const doDelete = async (t, force) => {
    setMsg(''); setMsgErr(false);
    try {
      const res = await api.delete(`/transactions/${t.id}${force ? '?force=true' : ''}`);
      setMsg(res.data.message);
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doDelete(t, true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  const openEdit = (t) => setEditTxn({ id: t.id, quantity: t.quantity, date: t.date, reason: t.reason || '', waste_category: t.waste_category || '', label: `${t.item_name} @ ${t.store_name}` });

  const saveEdit = (e) => { e.preventDefault(); doSaveEdit(false); };

  const doSaveEdit = async (force) => {
    setMsg(''); setMsgErr(false);
    try {
      await api.put(`/transactions/${editTxn.id}${force ? '?force=true' : ''}`, {
        quantity: parseInt(editTxn.quantity, 10),
        date: editTxn.date,
        reason: editTxn.reason,
        waste_category: editTxn.waste_category || undefined,
      });
      setMsg('¡Entrada de merma actualizada!');
      setEditTxn(null);
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doSaveEdit(true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  return (
    <div>
      <div className="page-header"><h1>Merma / Desperdicio</h1></div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Registrar Merma</h3>
        {msg && <div className={`alert ${msgErr ? 'alert-danger' : 'alert-success'}`}>{msg}</div>}
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            <div className="form-group">
              <label>Tienda</label>
              <select value={form.store_id} onChange={e => setForm({...form, store_id: e.target.value})} required>
                <option value="">Seleccionar tienda</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Artículo</label>
              <select value={form.item_id} onChange={e => setForm({...form, item_id: e.target.value})} required>
                <option value="">Seleccionar artículo</option>
                {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Categoría de Merma</label>
              <select value={form.waste_category} onChange={e => setForm({...form, waste_category: e.target.value})} required>
                <option value="">Seleccionar categoría</option>
                <option value="marketing">Marketing (eventos)</option>
                <option value="factory">Fábrica (defectuoso)</option>
                <option value="gifts">Regalos (a clientes)</option>
                <option value="other">Otro</option>
              </select>
            </div>
            <div className="form-group">
              <label>Cantidad</label>
              <input type="number" step="1" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Fecha</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required />
            </div>
          </div>
          <div className="form-group">
            <label>Motivo</label>
            <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} required rows={2} placeholder="p.ej. Vencido, dañado, contaminado..." />
          </div>
          <button type="submit" className="btn btn-primary">Registrar Merma</button>
        </form>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Entradas de Merma Recientes</h3>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortableTh colKey="date" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Fecha</SortableTh>
                <SortableTh colKey="store_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Tienda</SortableTh>
                <SortableTh colKey="item_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Artículo</SortableTh>
                <SortableTh colKey="quantity" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Cant.</SortableTh>
                <SortableTh colKey="waste_category" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Categoría</SortableTh>
                <th>Motivo</th>
                <SortableTh colKey="user_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Por</SortableTh>
                {(user?.role === 'admin' || user?.role === 'manager') && <th>Acciones</th>}
              </tr>
              <FilterRow
                columns={[null, 'store_name', 'item_name', null, 'waste_category', null, 'user_name', ...(user?.role === 'admin' || user?.role === 'manager' ? [null] : [])]}
                data={transactions}
                filters={filters}
                setFilter={setFilter}
                labelMaps={{ waste_category: CATEGORY_LABELS }}
              />
            </thead>
            <tbody>
              {paginated.map(t => (
                <tr key={t.id}>
                  <td>{formatDate(t.date)}</td><td>{t.store_name}</td><td>{t.item_name}</td>
                  <td>{t.quantity}</td>
                  <td><span className={`badge ${t.waste_category === 'other' ? 'badge-warning' : 'badge-secondary'}`}>{CATEGORY_LABELS[t.waste_category] || t.waste_category || '-'}</span></td>
                  <td>{t.reason}</td><td>{t.user_name}</td>
                  {(user?.role === 'admin' || user?.role === 'manager') && (
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(t)}>Editar</button>
                      {' '}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t)}>Eliminar</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
      </div>

      {editTxn && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Editar Entrada de Merma</h2>
            <p style={{ color: 'var(--text-2)', marginBottom: 12 }}>{editTxn.label}</p>
            <form onSubmit={saveEdit}>
              <div className="form-group">
                <label>Cantidad</label>
                <input type="number" step="1" min="1" value={editTxn.quantity} onChange={e => setEditTxn({...editTxn, quantity: e.target.value})} required autoFocus />
              </div>
              <div className="form-group">
                <label>Fecha</label>
                <input type="date" value={editTxn.date} onChange={e => setEditTxn({...editTxn, date: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Categoría de Merma</label>
                <select value={editTxn.waste_category} onChange={e => setEditTxn({...editTxn, waste_category: e.target.value})}>
                  <option value="">Seleccionar categoría</option>
                  <option value="marketing">Marketing (eventos)</option>
                  <option value="factory">Fábrica (defectuoso)</option>
                  <option value="gifts">Regalos (a clientes)</option>
                  <option value="other">Otro</option>
                </select>
              </div>
              <div className="form-group">
                <label>Motivo</label>
                <textarea value={editTxn.reason} onChange={e => setEditTxn({...editTxn, reason: e.target.value})} rows={2} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditTxn(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <InvalidateWarningModal
        data={invalidate}
        onCancel={() => setInvalidate(null)}
        onConfirm={() => { const r = invalidate.run; setInvalidate(null); r(); }}
      />
    </div>
  );
}
